import { useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import FormComp from "../../../Componentes/FormComp";
import MultiFileUploader from "../../../Componentes/Utils/MultiFileUploader";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";
import { formatBRLFromCents, parseCentsFromInput } from "../../../Funcoes/Money";

const toCaixaAlta = (str) => (typeof str === "string" ? str.toLocaleUpperCase("pt-BR") : str);

const ResumoLinha = styled.div`
    background-color: #f5f5f5;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 8px;
    font-size: 14px;
    color: #333;
    line-height: 1.5;
    p {
        margin: 4px 0;
    }
`;

const AnexosLabel = styled.h3`
    margin: 16px 0 10px;
    font-size: 16px;
    color: #555;
    text-align: center;
`;

const ModalNovaNFFaturamentoDireto = ({ faturamento, onFechar, onSalvo }) => {

    const { id: idPc } = useParams();
    const { closeModal } = useModal();
    const { notifySuccess, notifyError } = useToast();
    const api = useApi();

    const [saving, setSaving] = useState(false);
    const [arquivos, setArquivos] = useState([]);
    const [formValues, setFormValues] = useState({
        numNf: "",
        valorNf: 0,
        dataEmissao: new Date().toISOString().split("T")[0],
        observacoes: "",
    });

    const handleChange = (e) => {

        const { name, value } = e.target;

        if (name === "valorNf") {
            setFormValues((prev) => ({ ...prev, [name]: parseCentsFromInput(value) }));
            return;
        }

        if (name === "observacoes") {
            setFormValues((prev) => ({ ...prev, [name]: value }));
            return;
        }

        setFormValues((prev) => ({
            ...prev,
            [name]: typeof value === "string" ? toCaixaAlta(value) : value,
        }));

    };

    const fechar = () => {

        closeModal();
        if (typeof onFechar === "function") {
            onFechar();
        }

    };

    const handleSalvar = async (e) => {

        e.preventDefault();

        const numNf = toCaixaAlta(formValues.numNf.trim());
        if (!numNf) {
            notifyError("Informe o número da NF.");
            return;
        }

        if (formValues.valorNf <= 0) {
            notifyError("O valor da NF deve ser maior que zero.");
            return;
        }

        if (!formValues.dataEmissao) {
            notifyError("Informe a data de emissão.");
            return;
        }

        setSaving(true);

        try {

            const formData = new FormData();
            formData.append("id_pc", String(idPc));
            formData.append("id_faturamento", String(faturamento.id));
            formData.append("num_nf", numNf);
            formData.append("valor", String(formValues.valorNf));
            formData.append("data_emissao", formValues.dataEmissao);
            formData.append("observacoes", formValues.observacoes.trim());

            const { data } = await api.post(
                "/Painel/API/ControleMedicao/SaveNfFaturamentoDireto/",
                formData
            );

            const novoId = data?.id != null ? Number(data.id) : null;

            if (novoId != null && !Number.isNaN(novoId) && arquivos.length > 0) {

                const fdAnexo = new FormData();
                fdAnexo.append("id_nf", String(novoId));
                fdAnexo.append("id_pc", String(idPc));
                arquivos.forEach((item) => {
                    fdAnexo.append("files[]", item.file);
                });

                try {

                    await api.post(
                        "/Painel/API/ControleMedicao/AddAnexoNfFaturamentoDireto/",
                        fdAnexo
                    );
                    notifySuccess("NF e anexos salvos com sucesso.");

                } catch (errAnexo) {

                    const msgAnexo =
                        errAnexo?.response?.data?.message ||
                        "Erro ao enviar os anexos.";
                    notifyError(
                        typeof msgAnexo === "string"
                            ? `NF salva, mas falha nos anexos: ${msgAnexo}`
                            : "NF salva, mas falha ao enviar anexos."
                    );
                    console.error(errAnexo);

                }

            } else {

                notifySuccess("Nota fiscal salva com sucesso.");

            }

            fechar();
            if (typeof onSalvo === "function") {
                await onSalvo();
            }

        } catch (err) {

            const msg = err?.response?.data?.message || "Erro ao salvar. Tente novamente.";
            notifyError(typeof msg === "string" ? msg : "Erro ao salvar. Tente novamente.");
            console.error(err);

        } finally {

            setSaving(false);

        }

    };

    return (
        <Overlay state={true}>
            <Modal header close={fechar}>
                <Formulario onSubmit={handleSalvar}>
                    <ResumoLinha>
                        <p>
                            <b>DISCIPLINA:</b> {faturamento.disciplina || "—"}
                        </p>
                        <p>
                            <b>Nº PEDIDO / FORNEC.:</b> {faturamento.num_pedido_fornec || "—"}
                        </p>
                        <p>
                            <b>FORNECEDOR:</b> {faturamento.fornecedor || "—"}
                        </p>
                        <p>
                            <b>VALOR FAT. DIRETO (LINHA):</b>{" "}
                            {formatBRLFromCents(Number(faturamento.valor) || 0)}
                        </p>
                    </ResumoLinha>
                    <FormComp
                        title="NÚMERO DA NF"
                        type="text"
                        valor={formValues.numNf}
                        name="numNf"
                        changeFunc={handleChange}
                        required
                    />
                    <FormComp
                        title="VALOR DA NF"
                        type="text"
                        inputMode="numeric"
                        placeholder="R$ 0,00"
                        valor={formatBRLFromCents(formValues.valorNf)}
                        name="valorNf"
                        changeFunc={handleChange}
                        required
                    />
                    <FormComp
                        title="DATA DE EMISSÃO"
                        type="date"
                        valor={formValues.dataEmissao}
                        name="dataEmissao"
                        changeFunc={handleChange}
                        required
                    />
                    <FormComp
                        title="OBSERVAÇÕES"
                        type="textarea"
                        valor={formValues.observacoes}
                        name="observacoes"
                        changeFunc={handleChange}
                    />
                    <AnexosLabel>ANEXOS DA NF (OPCIONAL)</AnexosLabel>
                    <MultiFileUploader files={arquivos} setFiles={setArquivos} />
                    <FormComp
                        type="submit"
                        valor={saving ? "SALVANDO..." : "SALVAR NF"}
                        disabled={saving}
                    />
                </Formulario>
            </Modal>
        </Overlay>
    );

};

export default ModalNovaNFFaturamentoDireto;
